import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection
} from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { TranslationService, authInterceptor, provideEshavaAppConfig, provideEshavaAuthConfig, provideEshavaFormsLocale } from 'eshava-shared';

import { appRoutes } from './app.routes';
import { appSettings } from '@portal/settings';
import { environment } from '@portal/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),

    /**
     * **`portal` is not a decoration, it is the prefix every stored preference already has.**
     * Theme, language and the sidebar state were written as `portal.theme`, `portal.language` and
     * `portal.sidebar.collapsed` for as long as this application has existed. The package defaults
     * to `app`, so leaving this out would not have failed anything -- it would silently have shown
     * every user the system theme in English with an expanded sidebar, once, and looked like a bug
     * in the package.
     */
    provideEshavaAppConfig({ preferencePrefix: 'portal' }),

    /**
     * The OIDC configuration, and the whitelist of hosts the access token may be sent to.
     *
     * **`serviceOrigins` is derived from the base URLs, not from the endpoint list**, and that is the
     * decision worth keeping: the endpoint list grows, and every group added to it would silently
     * have travelled without a token. The base URLs are what "our own services" means -- Cardinal and
     * Accounts, and nothing else.
     */
    provideEshavaAuthConfig({
      authority: appSettings.authentication.authority,
      clientId: appSettings.authentication.clientId,
      scope: appSettings.authentication.scope,
      requireHttps: environment.oidc.requireHttps,
      origin: appSettings.authentication.redirectUri,
      serviceOrigins: originsOf([environment.baseUrls.cardinalUrl, environment.baseUrls.accountsUrl])
    }),

    /**
     * Zoneless, and `zone.js` is not in the polyfills at all. This is the reason the two shared
     * libraries from the other frontends are not used -- both rely on `zone.js` re-rendering them
     * after a `setTimeout`. Everything else in this application follows from it: state that has to
     * render is a signal.
     */
    provideZonelessChangeDetection(),

    provideRouter(
      appRoutes,
      /* Route `data` and parameters arrive as component inputs -- which is how the placeholder pages
         get their title key, and later how the product context reaches an area. */
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),

    /* Functional interceptor, no HTTP_INTERCEPTORS array. It attaches the bearer token only to the
       configured service origins -- the translation resources come from our own origin and need
       none, and sending it anywhere else would leak it. */
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),

    /**
     * The form library formats dates, and it must not read the application's language out of a
     * global -- so the application hands it in. A signal read rather than a value: switching the
     * language reformats every date field that is on screen.
     */
    provideEshavaFormsLocale(() => inject(TranslationService).language),

    /**
     * The language has to be loaded before the shell renders, otherwise the first frame shows raw
     * keys. Before login the browser setting decides; afterwards `PreferredLanguage` from the token
     * does, unless the user has chosen in the portal.
     */
    provideAppInitializer(() => {
      const translations = inject(TranslationService);
      return translations.use(translations.language());
    })
  ]
};

/**
 * Origins out of base URLs, skipping anything unparseable rather than throwing at start-up.
 *
 * A malformed base URL is a configuration mistake, and the honest failure for it is a request
 * arriving without a token -- not an application that refuses to boot.
 */
function originsOf(urls: readonly string[]): readonly string[] {
  return urls
    .map(url => {
      try {
        return new URL(url).origin;
      } catch {
        return null;
      }
    })
    .filter((origin): origin is string => origin !== null);
}
